<template>
  <section data-testid="sf-comment" :data-commentid="id">
    <article
      class="
        tw-grow
        tw-min-w-0
        tw-flex
        tw-flex-col
        tw-gap-2
        tw-px-6
        tw-py-2
        tw-relative
      "
    >
      <div
        v-if="!props.read"
        data-testid="unread-line"
        class="tw-absolute tw-inset-x-4 tw-w-0.5"
        :class="readLineClasses"
      />
      <div class="tw-grow tw-flex tw-flex-row tw-items-start tw-gap-2">
        <div class="tw-grow tw-space-y-1 tw-overflow-hidden tw-min-h-6">
          <div class="tw-flex tw-gap-2 tw-items-center">
            <span
              ref="authorRef"
              v-tooltip="authorTooltip"
              class="
                tw-font-medium
                tw-text-sm
                tw-whitespace-nowrap
                tw-text-ellipsis
                tw-overflow-hidden
                tw-leading-5
                tw-text-neutral-800
              "
            >
              {{ authorName }}
            </span>
            <time
              v-tooltip.timestamp="displayTime"
              class="tw-text-xs tw-text-sf-gray-light tw-whitespace-nowrap"
            >
              {{ displayTimeFormatted }}
            </time>
          </div>
        </div>
        <div class="tw-whitespace-nowrap">
          <slot name="actions" />
        </div>
      </div>
      <div class="tw-flex tw-flex-col tw-w-full tw-gap-2">
        <span
          v-if="!editing"
          ref="bodyTextRef"
          v-dompurify-html:allowTarget="renderedText"
          data-testid="comment-body-text"
          class="tw-text-sm tw-leading-5 sf-comment__text"
          :class="bodyTextClasses"
        ></span>
        <CommentForm
          v-if="editing"
          :id="id"
          :attachment-ids="attachmentIds"
          :text="text"
          :commentable-id="commentableId"
          :commentable-type="commentableType"
          :company-id="companyId"
          :ledger-id="ledgerId"
          :period-name="periodName"
          :period-private="periodPrivate"
          :variant="'transparent'"
        />
        <div v-if="!editing" class="tw-flex tw-flex-wrap tw-gap-1">
          <SfDocumentPill
            v-for="document in attachments"
            :id="document.id"
            :key="document.id"
            :viewing-url="document.url"
            :allow-delete="allowAttachmentDeletion"
            @delete="emit('deleteAttachment', document.id)"
          >
            <template #left-icon>
              <i
                class="
                  fa fa-file
                  tw-text-xs tw-leading-4 tw-text-sf-gray-light
                  hover:tw-text-sf-gray-light
                "
                data-testid="document-pill-file"
              ></i>
            </template>
            <template #default>
              {{ document.filename }}
            </template>
          </SfDocumentPill>
          <!-- Bottom actions slot for reply button in slim/presentation mode -->
          <div
            class="
              actions-container
              tw-absolute tw-bottom-2 tw-right-6 tw-hidden
              group-hover:tw-flex
              tw-bg-sf-neutral-subtle-2
              tw-shadow-[0_0_12px_12px_theme(colors.sf-neutral.subtle-2)]
              hover:tw-shadow-[0_0_12px_12px_theme(colors.sf-neutral.subtle-2)]
            "
          >
            <slot name="bottom-actions" />
          </div>
        </div>
      </div>
    </article>
  </section>
</template>
<script setup lang="ts">
import { computed, ref } from 'vue'

import CommentForm from '@app/application/communication/sections/communication/components/comments/comment-form.vue'
import SfDocumentPill from '@app/shared/vue/components/sf-document-pill.vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import communicationDate, {
  CommunicationDateFormats,
} from '@app/utils/communication-date'
import useDynamicTooltip from '@app/shared/vue/composables/use-dynamic-tooltip'
import usePulseMentionWarning, {
  type PulseMentionWarning,
} from '@app/shared/vue/composables/use-pulse-mention-warning'

interface Props {
  allowAttachmentDeletion?: boolean
  attachments: { url: string; filename: string; id: number }[]
  attachmentIds: Array<number>
  authorName: string
  commentableId: number
  commentableType: string
  companyId: number
  createdAt: string
  editing?: boolean
  editedAt: string | null
  id: number
  ledgerId: number
  read?: boolean
  renderedText: string
  text: string
  unreadTopOffset?: boolean
  unreadBottomOffset?: boolean
  pulseMentionWarning?: PulseMentionWarning | null
  periodName?: string
  periodPrivate?: boolean
}

const emit = defineEmits<{ deleteAttachment: [number] }>()

const props = defineProps<Props>()

const I18n = useI18n()

const bodyTextRef = ref<HTMLElement | null>(null)
usePulseMentionWarning(
  bodyTextRef,
  () => props.pulseMentionWarning,
  () => props.renderedText
)

const displayTime = computed(() => {
  return props.editedAt ? props.editedAt : props.createdAt
})

const displayTimeFormatted = computed(() => {
  return props.editedAt
    ? `${I18n.t('communication.cards.edited')} ${communicationDate(
        displayTime.value,
        CommunicationDateFormats.SHORT
      )}`
    : communicationDate(displayTime.value, CommunicationDateFormats.SHORT)
})

const authorRef = ref<HTMLElement | null>(null)
const authorTooltip = useDynamicTooltip(authorRef, props.authorName)

const bodyTextClasses = computed(() => {
  return props.read
    ? 'tw-text-sf-gray-darker'
    : 'tw-text-sf-gray tw-font-medium'
})

const readLineClasses = computed(() => {
  const result = []

  result.push(props.read ? 'tw-bg-transparent' : 'tw-bg-sf-danger-light-1')

  result.push(props.unreadTopOffset ? 'tw-top-2' : 'tw-top-0')
  result.push(props.unreadBottomOffset ? 'tw-bottom-2' : 'tw-bottom-0')

  return result
})
</script>

<style lang="scss">
.sf-comment__text {
  word-break: break-word;

  blockquote {
    margin-bottom: 16px;
  }

  ul,
  ol {
    padding-left: 16px;
    margin-bottom: 16px;
  }

  ul {
    list-style-type: disc;
  }

  ol {
    list-style-type: decimal;
  }

  &:last-child {
    margin-bottom: 0 !important; // Override for default bootstrap style
  }
}
</style>
